import type { vendorApiNames } from '../names';
import type { ApiKeyAuthentication } from './auth';
import type {
  BaseUpdateVendorApiSettings,
  BaseUrl,
  BaseVendorApi,
  PaginationSettings,
  RejectUnauthorized,
} from './base';
import type {
  VendorApiHttpAdvancedSettings,
  VendorApiHttpAgentSettings,
} from './http';

/** Columns that can be filtered on Meraki devices */
export const merakiFilterColumnKeys = ['platform', 'tag', 'networkId'] as const;
export type MerakiFilterColumnKey = (typeof merakiFilterColumnKeys)[number];

/** Operators supported by Meraki discovery filters */
export const merakiFilterOperators = ['eq', 'neq', 'reg', 'nreg'] as const;
export type MerakiFilterOperator = (typeof merakiFilterOperators)[number];

/** Logical operators on a MerakiFilterGroup */
export const merakiFilterGroupOperators = ['and', 'or'] as const;
export type MerakiFilterGroupOperator =
  (typeof merakiFilterGroupOperators)[number];

/** A single filter rule node within a MerakiFilterGroup tree */
export type MerakiFilterRule = {
  type: 'ruleFilter';
  column: { key: MerakiFilterColumnKey } | null;
  operator: MerakiFilterOperator | null;
  value: string;
};

/** A recursive AND/OR filter group, matching the AdvancedFilter component's GroupFilter shape */
export type MerakiFilterGroup = {
  type: 'groupFilter';
  operator: MerakiFilterGroupOperator;
  filters: (MerakiFilterGroup | MerakiFilterRule)[];
};

/**
 * Meraki API settings available from API
 */
export type PublicMerakiApiSettings = BaseVendorApi<
  (typeof vendorApiNames)['meraki']
> &
  RejectUnauthorized &
  BaseUrl &
  VendorApiHttpAgentSettings &
  VendorApiHttpAdvancedSettings &
  PaginationSettings & {
    apiVer: 'v1';
    organizations?: string[];
    merakiFilters?: MerakiFilterGroup;
  };

/**
 * Meraki API settings available in backend
 */
export type MerakiApiSettings = PublicMerakiApiSettings & ApiKeyAuthentication;

/**
 * Meraki API settings expected on API when updating
 */
export type UpdateMerakiApiSettings = BaseUpdateVendorApiSettings<
  PublicMerakiApiSettings,
  ApiKeyAuthentication
>;
